import { nextTick, ref, onBeforeUnmount, useRoute, onMounted, watch, computed, defineComponent, getCurrentInstance, useContext, useCssModule, } from '@nuxtjs/composition-api';
import { useTask } from 'vue-concurrency';
import { getElementTop, useAsyncRef, createHead } from '~/utils';
import { FRANCHISE_FILTERS } from '~/components/FranchiseFilters';
import { createGridChunks, getArticlesSize } from '~/composables';
import { presets } from '~/composables/grid/franchise-catalog';
import { useDuck } from '~/services';
import { HEADER_HEIGHTS } from '~/constants';
import { FRANCHISE_PROMO_CTA } from '~/pages/franchises/data';
/**
 * Duck service.
 */
const duck = useDuck();
/**
 * Columns
 */
export const columns = {
    desktop: 2,
    desktopMedium: 2,
    desktopWide: 3,
    mobile: 1,
    mobileMedium: 1,
    mobileWide: 1,
    tablet: 2,
};
export default defineComponent({
    name: 'PageFranchiseCatalog',
    scrollToTop: true,
    setup() {
        /**
         * Composables.
         */
        const context = useContext();
        const css = useCssModule();
        const route = useRoute();
        const viewport = context.$viewport;
        /**
         * Instance.
         */
        const instance = getCurrentInstance();
        const rootEl = ref();
        const rootElTop = ref(0);
        /**
         * Articles.
         */
        const articles = useAsyncRef(instance, 'articles');
        const isDone = useAsyncRef(instance, 'isDone');
        const total = useAsyncRef(instance, 'total');
        /**
         * Current preset.
         */
        const articlesCards = computed(() => getArticlesSize(viewport.breakpoint, {
            columns,
            presets,
        }));
        /**
         * Grids.
         */
        const grids = computed(() => createGridChunks(articles.value, articlesCards.value));
        const pageOffset = useAsyncRef(instance, 'pageOffset');
        /**
         * Filters
         */
        const filters = useAsyncRef(instance, 'filters');
        const filtersKey = ref(0);
        /**
         * Is some filters enabled
         */
        const isSomeFiltersEnabled = computed(() => {
            const values = Object.values(filters.value).reduce((values, value) => {
                if (value && value !== 'all') {
                    values.push(value);
                }
                return values;
            }, []);
            return values.length > 0;
        });
        /**
         * Request params
         */
        const requestParams = computed(() => {
            const obj = {};
            for (const key in filters.value) {
                const value = filters.value[key];
                if (key in FRANCHISE_FILTERS && value && value !== 'all') {
                    obj[`node[${key}]`] = value;
                }
            }
            return obj;
        });
        /**
         * Fetch articles task.
         */
        const fetchArticlesTask = useTask(function* (signal, shouldEmpty = false) {
            signal.addEventListener('abort', () => duck.abortRequest('getPubFranchises'));
            try {
                if (shouldEmpty) {
                    articles.value = [];
                    isDone.value = false;
                }
                const { data, headers } = yield duck.pub.getPubFranchises({
                    'list[limit]': String(articlesCards.value),
                    'list[offset]': shouldEmpty ? 0 : String(articles.value.length * (pageOffset.value + 1)),
                    'node[weight_sort]': true,
                    ...requestParams.value,
                });
                total.value = Number(headers?.get('x-total-count')) || 0;
                if (data.length < articlesCards.value) {
                    isDone.value = true;
                }
                if (!data.length) {
                    return;
                }
                articles.value = shouldEmpty ? data : [...articles.value, ...data];
            }
            catch (error) {
                console.error(error);
                isDone.value = true;
            }
            finally {
                if (shouldEmpty) {
                    yield nextTick();
                    window.scroll({ top: rootElTop.value, left: 0, behavior: 'smooth' });
                }
            }
        });
        function handleIntersect() {
            if (fetchArticlesTask.isRunning || isDone.value) {
                return;
            }
            fetchArticlesTask.perform();
        }
        function handleGridIntersect(gridKey) {
            const url = new URL(window.location);
            gridKey = gridKey + 1 + pageOffset.value;
            if (gridKey > 1) {
                url.searchParams.set('page', gridKey.toString());
            }
            else {
                url.searchParams.delete('page');
            }
            history.replaceState(null, '', url.pathname + '?' + url.searchParams.toString());
        }
        function handleUpdateFilters(data) {
            filters.value = { ...data };
        }
        function handleClickCardHeader() {
            if (route.value.name !== 'franchises-catalog') {
                return;
            }
            const { query } = route.value || {};
            const obj = {
                type_sort: 'new',
            };
            for (const key in query) {
                if (key in FRANCHISE_FILTERS && query[key]) {
                    obj[key] = query[key];
                }
            }
            filters.value = { ...obj };
            filtersKey.value += 1;
        }
        onBeforeUnmount(() => {
            window.removeEventListener('clickCardHeader', handleClickCardHeader);
        });
        onMounted(() => {
            window.addEventListener('clickCardHeader', handleClickCardHeader);
            watch(() => viewport.breakpoint, (bp) => {
                rootElTop.value = getElementTop(rootEl.value) - HEADER_HEIGHTS[bp] - 16;
            }, { immediate: true });
            const url = new URL(window.location);
            if (!url.searchParams.has('type_sort')) {
                // Сортировка по новым всегда активна по дефолту
                url.searchParams.set('type_sort', 'new');
                history.replaceState(null, '', url);
            }
            watch(() => filters.value, () => {
                const url = new URL(window.location);
                for (const key in filters.value) {
                    const value = filters.value[key];
                    if (value && value !== 'all') {
                        url.searchParams.set(key, value);
                    }
                    else {
                        url.searchParams.delete(key);
                    }
                }
                history.replaceState(null, '', url);
                fetchArticlesTask.perform(true);
            });
        });
        return {
            articlesCards,
            columns,
            css,
            fetchArticlesTask,
            filters,
            filtersKey,
            grids,
            handleIntersect,
            handleGridIntersect,
            handleUpdateFilters,
            isDone,
            isSomeFiltersEnabled,
            presets,
            requestParams,
            rootEl,
            rootElTop,
            total,
            FRANCHISE_PROMO_CTA,
        };
    },
    async asyncData(context) {
        const { app, env, error, route, query } = context;
        try {
            let isDone = false;
            let total = 0;
            const limit = getArticlesSize(app.$viewport.breakpoint, {
                columns,
                presets,
            });
            const pageNumber = Number(query.page) || 0;
            const pageOffset = pageNumber > 0 ? pageNumber - 1 : 0;
            const filters = {
                type_sort: 'new',
            };
            const requestParams = {
                'node[type_sort]': 'new',
            };
            for (const key in query) {
                if (key in FRANCHISE_FILTERS && query[key]) {
                    filters[key] = query[key];
                    requestParams[`node[${key}]`] = query[key];
                }
            }
            const { data: articles, headers } = await duck.pub.getPubFranchises({
                'list[limit]': String(limit),
                'list[offset]': String(limit * pageOffset),
                'node[weight_sort]': true,
                ...requestParams,
            });
            total = Number(headers?.get('x-total-count')) || 0;
            // if (!articles?.length) {
            //   throw new Error('Not found')
            // }
            if (articles.length < limit) {
                isDone = true;
            }
            const title = 'Каталог | Forbes Франшизы';
            const head = {
                canonical: env.HOST + route.path,
                title,
                url: env.HOST + route.path,
            };
            context.$page = app.$page = {
                title,
            };
            return {
                $page: context.$page,
                articles,
                filters,
                head,
                isDone,
                pageOffset,
                title,
                total,
            };
        }
        catch (e) {
            console.error(e);
            error({ statusCode: 404 });
        }
    },
    head() {
        return createHead(this.head);
    },
});
